import { useCallback, useEffect, useMemo, useState } from 'react'
import { BookOpen, Layers, UserRound } from 'lucide-react'
import Card from '../../components/Card'
import DataTable from '../../components/DataTable'
import DatePicker from '../../components/DatePicker'
import FilterBar from '../../components/FilterBar'
import { InfoNote } from '../../components/Form'
import OutcomeShare from '../../components/OutcomeShare'
import PageHeader from '../../components/PageHeader'
import PageLoader from '../../components/PageLoader'
import Select from '../../components/Select'
import StatStrip from '../../components/StatStrip'
import { useToast } from '../../components/toastContext'
import useListParams from '../../components/useListParams'
import { getReportsPage, tryGetProgramSummary } from './reportsService'
import { reportsStrings as S } from './strings'

/**
 * What this screen keeps in its own address — the programme and the two dates.
 *
 * `?program=` was already there: the programmes list links here from a row's report button,
 * and the picker starts on that programme instead of on nothing. THE DATES WERE NOT, and
 * they are half of what the report says — every share, every count and every course row is
 * for the window they name — so `/programs/reports?program=7` opened on the all-time figures
 * however the reader had narrowed them, and a Back out of the screen lost the window again.
 * `?program=7&from=2026-01-01` IS the report. Empty means all time and stays out of the URL.
 * Written with `replace`, so picking three programmes in a row does not fill the back button
 * with them. Module-level, as `useListParams` requires.
 *
 * `page` is the courses table's own — it is listed so the three setters below can put it
 * back to 1, which is the whole of the reset here: a new programme replaces the rows while
 * the table is unmounted behind the loader, so it never sees the change itself.
 */
const LIST_PARAMS = { program: '', from: '', to: '', page: 1 }

/**
 * تقرير البرامج التدريبية — the legacy `/LearningProgramsReports`.
 *
 * Built rather than wired (R5): the route rendered a placeholder.
 *
 * WHAT IS HERE AND WHAT IS NOT. The programme's outcome — the four shares, the counts, its
 * courses and its survey answers — comes from `GetProgramDetails`, which takes its id in the
 * body. The four DRILL-DOWN tables behind those shares (who passed, who failed, who did not
 * attend, who did not finish) read the programme id out of server session state that nothing
 * in this solution writes, so they answer for programme 0 whoever asks. They are not built,
 * and `COSMETIC_UI.md` says so rather than showing four empty tabs. `API_FINDINGS.md` §37.
 */
export default function ProgramReportsPage() {
  const toast = useToast()

  /* What the report is OF — in the address, so it survives a refresh and can be sent to
     someone. See components/useListParams.js. */
  const [params, setParams] = useListParams(LIST_PARAMS)

  const [programs, setPrograms] = useState([])
  const programId = params.program
  const fromDate = params.from
  const toDate = params.to
  /* One parameter each, patched rather than rewritten — the old setter replaced the whole
     search string, so choosing a programme would have taken the dates down with it — and
     each one starts the courses back at page 1, since it is a different set of courses. */
  const setProgramId = useCallback((next) => setParams({ program: next, page: 1 }), [setParams])
  const setFromDate = useCallback((next) => setParams({ from: next, page: 1 }), [setParams])
  const setToDate = useCallback((next) => setParams({ to: next, page: 1 }), [setParams])

  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    getReportsPage()
      .then((page) => setPrograms(page.programs))
      .catch((error) => toast.error(error?.message || S.actionFailed))
  }, [toast])

  useEffect(() => {
    if (!programId) {
      setSummary(null)
      return undefined
    }

    let cancelled = false
    setLoading(true)

    tryGetProgramSummary({ programId, fromDate, toDate }).then((outcome) => {
      if (cancelled) return
      setLoading(false)
      if (!outcome.ok) {
        setSummary(null)
        return toast.error(outcome.message || S.actionFailed)
      }
      setSummary(outcome.summary)
    })

    return () => {
      cancelled = true
    }
  }, [programId, fromDate, toDate, toast])

  const courseColumns = useMemo(
    () => [
      { key: 'name', header: S.courses.name, render: (row) => <bdi>{row.name}</bdi> },
      {
        key: 'startDate',
        header: S.courses.startDate,
        render: (row) => (
          <span dir="ltr" className="ud__date">
            {row.startDate}
          </span>
        ),
      },
      {
        key: 'endDate',
        header: S.courses.endDate,
        render: (row) => (
          <span dir="ltr" className="ud__date">
            {row.endDate}
          </span>
        ),
      },
      { key: 'topicsCount', header: S.courses.topics, align: 'center' },
      { key: 'success', header: S.courses.success, align: 'center' },
      { key: 'failure', header: S.courses.failure, align: 'center' },
      { key: 'absent', header: S.courses.absent, align: 'center' },
    ],
    [],
  )

  const surveyColumns = useMemo(
    () => [
      { key: 'question', header: S.survey.question, render: (row) => <bdi>{row.question}</bdi> },
      { key: 'answer', header: S.survey.answer, render: (row) => <bdi>{row.answer}</bdi> },
      { key: 'count', header: S.survey.count, align: 'center' },
    ],
    [],
  )

  return (
    <>
      <PageHeader title={S.title} subtitle={S.subtitle} />

      <FilterBar>
        <Select
          value={programId}
          onChange={setProgramId}
          options={programs}
          placeholder={S.filter.program}
          aria-label={S.filter.program}
        />
        <DatePicker value={fromDate} onChange={setFromDate} placeholder={S.filter.from} />
        <DatePicker value={toDate} onChange={setToDate} placeholder={S.filter.to} />
      </FilterBar>

      {!programId && (
        <InfoNote icon={BookOpen}>
          <bdi>{S.chooseProgram}</bdi>
        </InfoNote>
      )}

      {loading && <PageLoader />}

      {!loading && summary && (
        <>
          {/* The four shares this programme actually produced — the same component the
              programmes list hides for want of an aggregate. */}
          <OutcomeShare shares={summary.outcome} labels={S.shares} />

          <StatStrip
            label={S.stats.caption}
            items={[
              { id: 'courses', icon: BookOpen, label: S.stats.courses, value: summary.coursesCount, selectable: false },
              { id: 'groups', icon: Layers, label: S.stats.groups, value: summary.groupsCount, selectable: false },
              { id: 'users', icon: UserRound, label: S.stats.users, value: summary.usersCount, selectable: false },
            ]}
          />

          <Card title={S.courses.title}>
            <DataTable
              columns={courseColumns}
              rows={summary.courses}
              controls={false}
              /* The programme and the dates are the filter and they sit above the table,
                 where it cannot see them: page 3 of one programme's courses is not a page
                 of another's. The setters above carry most of this — the table is behind
                 the loader while the rows change — and this covers the rest. */
              resetKey={`${programId}|${fromDate}|${toDate}`}
              getRowId={(row) => row.id}
              emptyMessage={S.courses.empty}
            />
          </Card>

          {summary.survey.length > 0 && (
            <Card title={S.survey.title}>
              <DataTable
                /* Its own page in the address — `?page` belongs to the courses table above.
                   Remembering it instead would restore programme 7's page under programme 9,
                   since the key carries no programme. */
                pageParam="spage"
                columns={surveyColumns}
                rows={summary.survey}
                controls={false}
                /* Its answers change with the same three, so it starts over with them. */
                resetKey={`${programId}|${fromDate}|${toDate}`}
                getRowId={(row) => row.id}
              />
            </Card>
          )}
        </>
      )}
    </>
  )
}
